import { http } from '@/app/lib/http/client'
import type { PaginatedResponse } from '@/app/types/pagination'

export type CashHistoryView = 'sessions' | 'movements'
export type CashSessionStatus = 'OPEN' | 'CLOSED' | 'CANCELLED'
export type CashDifferenceFilter = 'shortage' | 'surplus' | 'exact'
export type CashMovementType =
  | 'OPENING'
  | 'SALE'
  | 'RETURN'
  | 'WITHDRAWAL'
  | 'DEPOSIT'
  | 'CLOSING_ADJUSTMENT'
export type CashMovementDirection = 'IN' | 'OUT'

export interface CashHistoryUser {
  id: number
  name: string
  last_name?: string | null
}

export interface CashHistoryBranch {
  id: number
  name: string
  is_active?: boolean
}

export interface CashHistoryRegister {
  id: number
  sucursal_id: number
  name: string
  code: string
  is_active: boolean
}

export interface CashDenomination {
  id: number
  denomination: string
  quantity: number
  total_amount: string
}

export interface CashSessionHistory {
  id: number
  status: CashSessionStatus
  sucursal: CashHistoryBranch | null
  cash_register: CashHistoryRegister | null
  opened_by: CashHistoryUser | null
  closed_by: CashHistoryUser | null
  cancelled_by: CashHistoryUser | null
  opening_cash_amount: string
  cash_sales_amount: string
  card_sales_amount: string
  credit_card_sales_amount: string
  transfer_sales_amount: string
  refund_amount: string
  withdraw_amount: string
  expected_cash_amount: string | null
  counted_cash_amount: string | null
  difference_amount: string | null
  cash_balance_amount: string
  movements_count: number
  sales_count: number
  notes: string | null
  opened_at: string
  closed_at: string | null
  cancelled_at: string | null
  opening_denominations?: CashDenomination[]
  closure_denominations?: CashDenomination[]
}

export interface CashSessionSummary {
  sessions_count: number
  status: {
    open: number
    closed: number
    cancelled: number
  }
  opening_total: string
  cash_sales_total: string
  card_sales_total: string
  credit_card_sales_total: string
  transfer_sales_total: string
  refund_total: string
  withdrawal_total: string
  expected_total: string
  counted_total: string
  difference_total: string
}

export interface CashMovementReference {
  id: number
  status: string
  ticket_number?: string
  return_number?: string
}

export interface CashMovementHistory {
  id: number
  cash_register_session_id: number
  movement_type: CashMovementType
  direction: CashMovementDirection
  amount: string
  concept: string
  user: CashHistoryUser | null
  sucursal: CashHistoryBranch | null
  cash_register: CashHistoryRegister | null
  session_status: CashSessionStatus
  sale: CashMovementReference | null
  sale_return: CashMovementReference | null
  occurred_at: string
}

export interface CashMovementSummaryRow {
  movement_type: CashMovementType
  direction: CashMovementDirection
  amount: string
}

export interface CashMovementSummary {
  movements_count: number
  incoming_total: string
  outgoing_total: string
  net_total: string
  by_type: CashMovementSummaryRow[]
}

export interface CashSessionFilters {
  page?: number
  per_page?: number
  search?: string
  sucursal_id?: number
  cash_register_id?: number
  status?: CashSessionStatus
  difference?: CashDifferenceFilter
  from?: string
  to?: string
}

export interface CashMovementFilters {
  page?: number
  per_page?: number
  search?: string
  sucursal_id?: number
  cash_register_id?: number
  cash_register_session_id?: number
  movement_type?: CashMovementType
  direction?: CashMovementDirection
  from?: string
  to?: string
}

export interface CashSessionHistoryResponse {
  summary: CashSessionSummary
  sessions: PaginatedResponse<CashSessionHistory>
}

export interface CashMovementHistoryResponse {
  summary: CashMovementSummary
  movements: PaginatedResponse<CashMovementHistory>
}

export interface CashSessionDetailResponse {
  session: CashSessionHistory
  movement_summary: CashMovementSummary
  movements: PaginatedResponse<CashMovementHistory>
}

export const cashHistoryService = {
  async getSessions(params: CashSessionFilters): Promise<CashSessionHistoryResponse> {
    const response = await http.get('/pos/register-sessions', { params })
    return response.data.data
  },

  async getSession(id: number, page = 1, perPage = 10): Promise<CashSessionDetailResponse> {
    const response = await http.get(`/pos/register-sessions/${id}`, {
      params: { page, per_page: perPage },
    })
    return response.data.data
  },

  async exportSession(id: number): Promise<Blob> {
    const response = await http.get(`/pos/register-sessions/${id}/export`, {
      responseType: 'blob'
    })
    return response.data
  },

  async getMovements(params: CashMovementFilters): Promise<CashMovementHistoryResponse> {
    const response = await http.get('/pos/cash-movements/history', { params })
    return response.data.data
  },
}
